import { z } from "zod";
export const InputTypeZ = z.enum([
    "youtube",
    "arxiv",
    "github",
    "huggingface",
    "blog",
    "community",
    "topic",
]);
export const IntentZ = z.object({
    purpose: z.string().optional().default(""),
    focus: z.string().optional().default(""),
    audience_level: z.string().optional().default(""),
    notes: z.string().optional().default(""),
    interpretation: z.string().optional().default(""),
});
export const SourceZ = z.object({
    n: z.number().int().positive(),
    adapter: z.string(),
    type: z.string(),
    url: z.string(),
    title: z.string(),
    meta: z.record(z.unknown()).optional(),
    fetched_at: z.string().optional(),
});
export const SourcesFileZ = z.object({
    sources: z.array(SourceZ),
    intent: IntentZ.optional(),
    input: z.string(),
    input_type: InputTypeZ,
    created: z.string(),
    output_notion_url: z.string().optional(),
});
export const ChapterZ = z.object({
    title: z.string(),
    start: z.string(),
    end: z.string(),
    summary: z.string().optional().default(""),
});
export const SessionSummaryZ = z.object({
    slug: z.string(),
    title: z.string(),
    created: z.string(),
    input: z.string(),
    input_type: InputTypeZ,
    intent_mode: z.enum(["user", "assumed"]).optional(),
    intent_purpose: z.string().optional().default(""),
    source_count: z.number().int().nonnegative().default(0),
    has_viz: z.boolean().default(false),
    notion_url: z.string().optional(),
    // auth + tiered sessions
    model: z.string().nullable().optional(),
    visibility: z.enum(["public", "private"]).optional(),
    status: z.enum(["running", "done", "error"]).optional(),
});
export const FindingZ = z.object({
    text: z.string(),
    source_ids: z.array(z.number().int().positive()),
    timecode: z.string().optional(),
    quote: z.string().optional(),
});
export const ReportBlockZ = z.object({
    level: z.union([z.literal(2), z.literal(3)]),
    heading: z.string(),
    body: z.string(),
});
export const ReportZ = z.object({
    slug: z.string(),
    title: z.string(),
    created: z.string(),
    input: z.string(),
    input_type: InputTypeZ,
    intent_mode: z.enum(["user", "assumed"]).optional(),
    notion_url: z.string().optional(),
    intent: IntentZ.optional(),
    intro: z.string().default(""),
    blocks: z.array(ReportBlockZ).default([]),
    sources: z.array(SourceZ),
    // tiered-session metadata — the /sessions/:slug endpoint spreads the row summary,
    // so these accompany the report body.
    model: z.string().nullable().optional(),
    visibility: z.enum(["public", "private"]).optional(),
    source_count: z.number().int().nonnegative().optional(),
});
export const VizFlagsZ = z.object({
    slides: z.boolean().default(false),
    diagrams: z.boolean().default(false),
    images: z.boolean().default(false),
    images_strategies: z.array(z.string()).default([]),
    judge: z.boolean().default(false),
    preset: z.string().nullable().default(null),
    brand_image: z.string().nullable().default(null),
    fresh: z.boolean().default(false),
});
export const VizChartZ = z.object({
    id: z.string(),
    title: z.string(),
    png_rel: z.string(),
});
export const VizDiagramZ = z.object({
    id: z.string(),
    title: z.string(),
    placement: z.string().optional(),
    evidence_src_ids: z.array(z.number()).default([]),
});
export const VizFileZ = z.object({
    slug: z.string(),
    generated_at: z.string(),
    flags: VizFlagsZ,
    charts: z.array(VizChartZ).default([]),
    diagrams: z.array(VizDiagramZ).default([]),
    images: z.array(z.object({
        id: z.string(),
        section: z.string().optional(),
        strategy: z.string().optional(),
        path_rel: z.string(),
        source_ids: z.array(z.number()).default([]),
    })).default([]),
    slides: z.object({
        md: z.string().nullable().default(null),
        pptx: z.string().nullable().default(null),
        pdf: z.string().nullable().default(null),
    }).optional(),
    judge: z.object({
        verdict: z.enum(["PASS", "FAIL"]).nullable().default(null),
        total: z.number().default(0),
        passes: z.number().default(0),
        file: z.string().nullable().default(null),
    }).optional(),
    lint: z.object({
        violations: z.number().default(0),
        warnings: z.number().default(0),
        file: z.string().nullable().default(null),
    }).optional(),
    failures: z.object({
        charts: z.array(z.unknown()).default([]),
        slides: z.array(z.unknown()).default([]),
        images: z.array(z.unknown()).default([]),
    }).optional(),
});
export const FollowupEntryZ = z.object({
    ts: z.string(),
    question: z.string(),
    answer: z.string(),
    new_sources: z.array(z.object({
        title: z.string(),
        url: z.string(),
        note: z.string().optional(),
    })).default([]),
});
export const DepsHealthZ = z.object({
    db: z.boolean().default(false),
    yt_dlp: z.boolean().default(false),
    gh: z.boolean().default(false),
    jq: z.boolean().default(false),
    npx: z.boolean().default(false),
    bun: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// Engine/web coevolution contracts
//
// Three machine-readable surfaces let `research-engine-web` evolve alongside
// the `research-engine` plugin without lockstep deploys:
//   - EngineCapabilityManifest — what the engine can do right now (runners,
//     input types, flags, artifacts, phases, dependencies, skill packs).
//   - ResearchSessionManifest  — what one run actually produced (per-session).
//   - EvolutionProposal        — engine-suggested changes the web can surface.
//
// All object schemas `.passthrough()` and unknown enum-like fields are kept as
// free strings so a newer engine emitting extra fields / future artifact kinds
// never breaks an older web/API. Defaults keep older/sparser payloads valid.
// ---------------------------------------------------------------------------
const ExtensionZ = z.record(z.unknown()).optional();
export const CapabilityStabilityZ = z.enum(["stable", "experimental", "hidden"]);
export const CapabilityVisibilityZ = z.enum(["user", "admin", "internal"]);
export const ArtifactRendererZ = z.enum(["markdown", "json", "text", "image", "download", "custom"]);
export const PhaseStatusZ = z.enum(["pending", "running", "done", "error", "skipped"]);
export const RunnerCapabilityZ = z.object({
    id: z.string(),
    label: z.string().optional(),
    available: z.boolean().default(false),
    models: z.array(z.string()).default([]),
    accepts_input: z.boolean().optional(),
}).passthrough();
export const InputTypeCapabilityZ = z.object({
    id: z.string(),
    label: z.string().optional(),
    stable: z.boolean().default(true),
}).passthrough();
export const FlagCapabilityZ = z.object({
    id: z.string(),
    label: z.string().optional(),
    value_type: z.enum(["boolean", "string", "number"]).default("boolean"),
    visibility: CapabilityVisibilityZ.default("user"),
    stability: CapabilityStabilityZ.default("stable"),
}).passthrough();
export const ArtifactCapabilityZ = z.object({
    kind: z.string(),
    title: z.string(),
    renderer: ArtifactRendererZ,
    stable: z.boolean().default(true),
}).passthrough();
export const PhaseCapabilityZ = z.object({
    id: z.string(),
    label: z.string(),
    order: z.number().optional(),
}).passthrough();
export const DependencyCapabilityZ = z.object({
    id: z.string(),
    label: z.string().optional(),
    available: z.boolean().default(false),
    detail: z.string().optional(),
}).passthrough();
export const SkillPackCapabilityZ = z.object({
    repo: z.string(),
    ref: z.string().optional(),
    path: z.string().optional(),
    status: z.string(),
}).passthrough();
export const ExperimentCapabilityZ = z.object({
    id: z.string(),
    label: z.string().optional(),
    enabled: z.boolean().default(false),
}).passthrough();
export const EngineCapabilityManifestZ = z.object({
    schema_version: z.literal("1"),
    engine: z.object({
        name: z.string(),
        version: z.string().optional(),
        git_ref: z.string().optional(),
        source: z.string().optional(),
    }).passthrough(),
    generated_at: z.string(),
    runners: z.array(RunnerCapabilityZ).default([]),
    input_types: z.array(InputTypeCapabilityZ).default([]),
    flags: z.array(FlagCapabilityZ).default([]),
    artifacts: z.array(ArtifactCapabilityZ).default([]),
    phases: z.array(PhaseCapabilityZ).default([]),
    dependencies: z.array(DependencyCapabilityZ).default([]),
    skill_packs: z.array(SkillPackCapabilityZ).default([]),
    experiments: z.array(ExperimentCapabilityZ).default([]),
    extensions: ExtensionZ,
}).passthrough();
export const ResearchSessionManifestZ = z.object({
    schema_version: z.literal("1"),
    slug: z.string(),
    engine: z.object({
        runner: z.string(),
        model: z.string().optional(),
        engine_version: z.string().optional(),
        git_ref: z.string().optional(),
        skill_packs: z.array(SkillPackCapabilityZ).default([]),
    }).passthrough(),
    input: z.object({
        raw: z.string(),
        input_type: z.string(),
        intent_mode: z.enum(["user", "assumed"]).optional(),
    }).passthrough(),
    phases: z.array(z.object({
        id: z.string(),
        label: z.string(),
        status: PhaseStatusZ,
        started_at: z.string().optional(),
        ended_at: z.string().optional(),
        summary: z.string().optional(),
    }).passthrough()).default([]),
    artifacts: z.array(z.object({
        id: z.string(),
        kind: z.string(),
        title: z.string(),
        path: z.string(),
        mime: z.string().optional(),
        renderer: ArtifactRendererZ,
        stable: z.boolean().default(false),
    }).passthrough()).default([]),
    quality: z.object({
        citations_verified: z.boolean().optional(),
        source_count: z.number().optional(),
        critique_applied: z.boolean().optional(),
        judge_verdict: z.enum(["PASS", "FAIL"]).nullable().optional(),
        warnings: z.array(z.string()).default([]),
    }).passthrough(),
    failures: z.array(z.object({
        phase: z.string().optional(),
        adapter: z.string().optional(),
        message: z.string(),
        recoverable: z.boolean(),
    }).passthrough()).default([]),
}).passthrough();
export const EvolutionProposalZ = z.object({
    schema_version: z.literal("1"),
    id: z.string(),
    title: z.string(),
    kind: z.enum([
        "new_adapter",
        "new_runner",
        "new_artifact",
        "prompt_improvement",
        "quality_gate",
        "ui_suggestion",
        "schema_change",
    ]),
    summary: z.string(),
    compatibility: z.enum(["backward-compatible", "requires-web-change", "breaking"]),
    suggested_web_surfaces: z.array(z.string()).default([]),
    required_contract_changes: z.array(z.string()).default([]),
    sample_sessions: z.array(z.string()).default([]),
    promotion_criteria: z.array(z.string()).default([]),
}).passthrough();
